import React, {useEffect, useState} from 'react';
import {Modal, Button, Form, Dropdown, Message} from 'semantic-ui-react';
import {toast} from 'react-semantic-toasts';
import {useDataInspector} from '../../../providers/DataInspectorProvider';
import {useDataMaintenance} from '../../../providers/DataMaintenanceProvider';
import {useUtilityAccounts} from '../../../providers/UtilityAccountProvider';
import TimeZones from '../Timezones.json';
import './UtilityAccounts.css';

const formStyle = {
  field: {
    display: 'flex',
    alignItems: 'center',
  },
  label: {
    fontSize: '1.2rem',
    marginRight: '1rem',
    width: '40%',
  },
  input: {
    width: '100%',
  },
};

const accountTypeOptions = [
  {key: 'electric', text: 'Electric', value: 'electric'},
  {key: 'gas', text: 'Gas', value: 'gas'},
];

export const UtilityAccountModal = ({
  open,
  setOpen,
  type,
  customer,
  selectedUtilityAccount,
  setLoading,
  onSuccess
}) => {
  const [, setSelectedCustomer] = useState(null);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [weatherStationOptions, setWeatherStationOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState('');
  const [weatherStation, setWeatherStation] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [utilityRate, setUtilityRate] = useState(null);
  const [errors, setErrors] = useState([]);

  const {createUtilityAccount, updateUtilityAccount} = useUtilityAccounts();
  const {getDataInspectorEquipmentByGroup, equipment} = useDataInspector();
  const {weatherStations, getWeatherStations} = useDataMaintenance();

  useEffect(() => {
    getWeatherStations();
  }, [getWeatherStations]);

  useEffect(() => {
    let tempOptions = [];
    setWeatherStationOptions([]);
    weatherStations.forEach(weatherStation => {
      tempOptions.push({
        key: weatherStation.id,
        value: weatherStation.id,
        text: weatherStation.name,
      });
    });
    setWeatherStationOptions(tempOptions);
  }, [weatherStations]);

  const isCreate = () => {
    if (type === 'Create') {
      return true;
    }
    return false;
  };

  const isEdit = () => {
    if (type === 'Edit') {
      return true;
    }
    return false;
  };
  
  const clearForm = () => {
    setName('');
    setGroupId('');
    setSelectedEquipment('');
    setAccountNumber('');
    setAccountType('');
    setWeatherStation('');
    setTimeZone('');
    setUtilityRate(null);
    setErrors([]);
  }

  const handleCreate = async () => {
    if (!name.trim() || !selectedEquipment || !accountNumber.trim() || !accountType || !weatherStation || !timeZone) {
      setErrors(['All fields are required and must not be empty.']);
      return;
    }
    
    setIsSubmitting(true);
    setErrors([]);
    
    try {
      await createUtilityAccount({
        name: name,
        group_id: groupId,
        object_id: selectedEquipment,
        num: accountNumber.toString(),
        type: accountType,
        weatherstation_id: weatherStation,
        timeZone: timeZone,
        utilityRate: utilityRate,
      });

      await onSuccess();
      setOpen(false);
      toast({ type: 'success', title: 'Success', description: 'Utility account created successfully', animation: 'fade down', time: 4000 });
    } catch (error) {
      console.error('Failed to create utility account: ', error);
      setErrors([error.message || 'Failed to create utility account. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!name.trim() || !selectedEquipment || !accountNumber.trim() || !accountType || !weatherStation || !timeZone) {
      setErrors(['All fields are required and must not be empty.']);
      return;
    }
    
    setIsSubmitting(true);
    setErrors([])
    
    try {
      await updateUtilityAccount({
        id: selectedUtilityAccount.id,
        name: name,
        group_id: groupId,
        object_id: selectedEquipment,
        num: accountNumber.toString(),
        type: accountType,
        weatherstation_id: weatherStation,
        timezone: timeZone,
        utilityRate: utilityRate,
      });

      await onSuccess();
      setOpen(false);
      toast({ type: 'success', title: 'Success', description: 'Utility account updated successfully', animation: 'fade down', time: 4000 });
    } catch (error) {
      console.error('Failed to edit account: ', error);
      setErrors([error.message || 'Failed to update utility account. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEquipmentChange = equipmentId => {
    setSelectedEquipment(equipmentId);
    setSelectedCustomer(equipmentId);

    setAccountNumber(equipment.find(equipment => equipment.id === equipmentId).num);
  };

  useEffect(() => {
    if (typeof selectedUtilityAccount !== 'undefined' && selectedUtilityAccount.id) {
      getDataInspectorEquipmentByGroup({
        groupIds: [selectedUtilityAccount.group_id],
        showStale: false
      }).catch(err => {
        console.error('Failed to load equipment:', err);
        setEquipmentOptions([]);
      });

      setName(selectedUtilityAccount.name);
      setGroupId(selectedUtilityAccount.group_id);
      setAccountNumber(selectedUtilityAccount.num);
      setAccountType(selectedUtilityAccount.type);
      setWeatherStation(selectedUtilityAccount.weatherStationId);
      setSelectedEquipment(selectedUtilityAccount.object_id);
      setTimeZone(selectedUtilityAccount.timezone);
      setUtilityRate(selectedUtilityAccount.utilityRate);
    } else if (typeof customer !== 'undefined' && customer.groupId) {
      getDataInspectorEquipmentByGroup({
        groupIds: [customer.groupId],
        showStale: false
      }).catch(err => {
        console.error('Failed to load equipment:', err);
        setEquipmentOptions([]);
      });

      setName(customer.companyName);
      setGroupId(customer.groupId);

      setAccountNumber('');
      setAccountType('');
      setWeatherStation('');
      setSelectedEquipment('');
      setTimeZone('');
      setUtilityRate('0.00');
    }
  }, [selectedUtilityAccount, customer, getDataInspectorEquipmentByGroup]);

  useEffect(() => {
    let tempOptions = [];
    setEquipmentOptions([]);
    equipment.forEach(equipmentItem => {
      tempOptions.push({
        key: equipmentItem.id,
        value: equipmentItem.id,
        text: `(${equipmentItem.id}) ${equipmentItem.name}`,
      });
    });
    setEquipmentOptions(tempOptions);
  }, [equipment]);

  return (
      <Modal
        size={'tiny'}
        closeIcon={!isSubmitting}
        onClose={() => {
          if (isSubmitting) return;
          setOpen(false);
          setErrors([]);
          clearForm();
        }}
        onOpen={() => setOpen(true)}
        open={open}
        style={{textAlign: 'left'}}
      >
        <Modal.Header>{isEdit() ? 'Edit ' : 'Create '} Account</Modal.Header>
        <Modal.Content>
          <div className="utility-accounts-container">
            <Form error={errors.length > 0} loading={isSubmitting}>
              <Form.Field style={formStyle.field}>
                <label style={formStyle.label}>Name</label>
                <input
                  placeholder="Name"
                  style={formStyle.input}
                  className="utilityInput"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  disabled={isSubmitting}
                />
              </Form.Field>
              <Form.Field style={formStyle.field}>
                <label style={formStyle.label}>Equipment</label>
                <Dropdown
                  label="Equipment"
                  placeholder="Equipment"
                  options={equipmentOptions}
                  value={selectedEquipment}
                  onChange={(e, {value}) => handleEquipmentChange(value)}
                  disabled={isSubmitting}
                  selection
                  search
                />
              </Form.Field>
              <Form.Field style={formStyle.field}>
                <label style={formStyle.label}>Equipment Number</label>
                <input
                  placeholder="Equipment Number"
                  style={formStyle.input}
                  className="utilityInput"
                  value={accountNumber}
                  onChange={e => setAccountNumber(e.target.value)}
                  disabled={isSubmitting}
                />
              </Form.Field>
              <Form.Field style={formStyle.field}>
                <label style={formStyle.label}>Account Type</label>
                <Dropdown
                  label="Equipment"
                  placeholder="Equipment"
                  options={accountTypeOptions}
                  value={accountType}
                  onChange={(e, {value}) => setAccountType(value)}
                  disabled={isSubmitting}
                  selection
                  search
                />
              </Form.Field>
              <Form.Field style={formStyle.field}>
                <label style={formStyle.label}>Weather Station</label>
                <Dropdown
                  label="Weather Station"
                  placeholder="Weather Station"
                  options={weatherStationOptions}
                  value={weatherStation}
                  onChange={(e, {value}) => setWeatherStation(value)}
                  disabled={isSubmitting}
                  selection
                  search
                />
              </Form.Field>
              <Form.Field style={formStyle.field}>
                <label style={formStyle.label}>Time Zone</label>
                <Dropdown
                  label="Time Zone"
                  placeholder="Time Zone"
                  options={TimeZones}
                  value={timeZone}
                  onChange={(e, {value}) => setTimeZone(value)}
                  disabled={isSubmitting}
                  selection
                  search
                />
              </Form.Field>
              <Form.Group className='utility-accounts-error'>
                <Message error>
                  {errors.map((err, i) => <p key={i}>{err}</p>)}
                </Message>
              </Form.Group>
            </Form>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button 
              onClick={() => {
                setOpen(false);
                setErrors([]);
              }}
              disabled={isSubmitting}
          >
            Cancel
          </Button>
          {isCreate() ? (
            <Button 
                className={'ui primary button'} 
                onClick={handleCreate}
                loading={isSubmitting}
                disabled={isSubmitting}
            >
              Submit
            </Button>
          ) : (
            <Button 
                className={'ui primary button'} 
                onClick={handleEdit}
                loading={isSubmitting}
                disabled={isSubmitting}
            >
              Edit
            </Button>
          )}
        </Modal.Actions>
      </Modal>
  );
};
